<script setup>
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore'
import { splitKeywordParts, transformMarkedText } from '@/composables/splitSentence'
import IconLike from '@/components/svg/IconLike.vue'
import { patchAnalysisLike, gatchAnalysisLike } from '@/api/baseServiceApi'
import { computed, ref, onMounted } from 'vue'

const analysisDataStore = useAnalysisDataStore()

// props 정의
const props = defineProps({
  guestUserId: {
    type: Number,
    default: 0,
  },
})

// 직업추천 데이터를 분리한 computed 속성들
const priorityJobFields = computed(() => analysisDataStore.pData?.직업추천?.우선추천분야 || {})
const generalJobFields = computed(() => analysisDataStore.pData?.직업추천?.일반추천분야 || {})

// 상단 데이터
const personCategory = computed(() => analysisDataStore.pData?.전체성향요약?.카테고리 || '')
// const jobSummary = computed(() => analysisDataStore.pData?.직업요약)
const jobSummary = computed(() =>{
  const sentences = splitBySentence(analysisDataStore.pData?.직업요약 || '')
  return sentences.map(sentence => transformMarkedText(sentence))
})

// 직업 추천 좋아요
const likeStates = ref({})

/** 좋아요 클릭/해제 */
const toggleLike = async (category) => {
  try {
    await patchLikeJob(category)
    // 토글 상태 업데이트
    likeStates.value[category] = !likeStates.value[category]
  } catch (error) {
    console.error('좋아요 토글 실패:', error)
  }
}

/** 좋아요 정보 전달 */
const patchLikeJob = async (category) => {
  const result = await patchAnalysisLike('BASE_LUCK_JOB', props.guestUserId, category)
  return result
}

/** 좋아요 정보 조회 */
const getLikeJob = async (category) => {
  try {
    const result = await gatchAnalysisLike('BASE_LUCK_JOB', props.guestUserId, category)
    return result
  } catch (error) {
    console.error('좋아요 정보 조회 실패:', error)
    return { likeChecked: false }
  }
}

// 컴포넌트 마운트 시 좋아요 상태 초기화
onMounted(async () => {
  if (priorityJobFields.value && Object.keys(priorityJobFields.value).length > 0) {
    for (const category of Object.keys(priorityJobFields.value)) {
      try {
        const likeInfo = await getLikeJob(category)
        likeStates.value[category] = likeInfo?.likeChecked || false
      } catch (error) {
        likeStates.value[category] = false
      }
    }
  }
})
</script>

<template>
  <div class="round-tit-box st-personality-job">
    👍 내 성향이 빛을 발할 수 있는 우선 추천 직업들
  </div>

  <div class="job-recommendation-top">
    <div class="txt" v-for="(html, idx) in jobSummary" :key="idx">
      <span v-html="html" />
    </div>
  </div>
  <!-- 우선추천분야 -->
  <div
    v-if="priorityJobFields && Object.keys(priorityJobFields).length > 0"
    class="job-recommendation-section st-orange"
  >
    <!-- <h3 class="section-title">우선추천분야</h3> -->
    <div v-for="(jobs, category) in priorityJobFields" :key="category" class="job-category">
      <div class="category-title">
        <div class="h3">{{ category }}</div>
        <div class="job-list">
          <span v-for="(job, index) in jobs" :key="job" class="job-item">
            {{ job }}{{ index < jobs.length - 1 ? ', ' : '' }}
          </span>
        </div>
      </div>
      <div class="like-box">
        <button class="like-btn" @click="toggleLike(category)">
          <IconLike :class="likeStates[category] ? 'active' : 'default'" />
        </button>
      </div>
    </div>
  </div>

  <!-- 일반추천분야 -->
  <div class="round-tit-box st-personality-job">
    ☝️ 함께 고려해볼 수 있는 다양한 진로 방향도 제안드려요.
  </div>
  <div
    v-if="generalJobFields && Object.keys(generalJobFields).length > 0"
    class="job-recommendation-section"
  >
    <!-- <h3 class="section-title">일반추천분야</h3> -->
    <div v-for="(jobs, category) in generalJobFields" :key="category" class="job-category">
      <div class="category-title">
        <div class="h3">{{ category }}</div>
        <div class="job-list">
          <span v-for="(job, index) in jobs" :key="job" class="job-item">
            {{ job }}{{ index < jobs.length - 1 ? ', ' : '' }}
          </span>
        </div>
      </div>
      <div class="like-box"></div>
    </div>
  </div>

  <div class="gap-large"></div>
</template>

<style lang="scss">
@import '@/assets/nangman_style/_analysis_main_color.scss';
.st-personality-job {
  margin-bottom: 12px !important;
}
.job-recommendation-section {
  margin-bottom: 16px;
}

.section-title {
  font-size: 1.2rem;
  font-weight: bold;
  margin-bottom: 1rem;
  color: #333;
}
.job-recommendation-section {
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  border-radius: 12px;
  border: 1px solid var(--Gray-200, #f8f8f8);
  background: var(--Gray-200, #f8f8f8);
  &.st-orange {
    border: 1px solid $analysis-personality-border;
    background: $analysis-personality-light;
  }
  .job-category {
    display: flex;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
    border-bottom: 1px solid var(--Gray-300, #ebebeb);
    padding-bottom: 8px;
    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }
  }
  .h3 {
    color: var(--Gray-800, #333);
    font-family: Pretendard;
    font-size: 15px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px; /* 133.333% */
    margin-bottom: 4px;
  }
  .job-list {
    color: var(--Gray-600, #757575);
    font-family: Pretendard;
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px; /* 123.077% */
  }
  .like-box {
    margin-left: 20px;

    .default {
      color: var(--Gray-300, #ebebeb);
    }
    .active {
      color: var(--Orange-300, #eb4c10);
    }
  }
}
.job-recommendation-top {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
  margin-bottom: 16px;
  .tit {
    color: $analysis-personality;
    font-family: Pretendard;
    font-size: 15px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px; /* 160% */
  }
  .txt {
    color: var(--Gray-800, #333);
    font-family: Pretendard;
    font-size: 15px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px; /* 160% */
    .key-snetence {
      color: $analysis-personality;
    }
  }
}
</style>
